// Shared thermal calculation types
// Used by heat_source_sink and heat_exchanger vertices

import { z } from 'zod';
import { ValueWithUnitSchema } from './common';

/**
 * Thermal calculation mode for heat devices.
 * Determines which quantity the solver calculates (the unknown).
 * The stored value fields are the known user inputs.
 *
 * Source: C++ thermal_calculation_t enum + thermal_calculation::impl
 * BOOST_CLASS_VERSION: 1 (v21.0)
 */
export const ThermalCalculationSchema = z.object({
  mode: z.enum([
    'calculate_heat_transfer_rate',
    'calculate_flow_rate',
    'calculate_inlet_temperature',
    'calculate_outlet_temperature',
    'none',
  ]),
  // Known input values (populated based on mode):
  heat_transfer_rate: ValueWithUnitSchema.optional(),
  flow_rate: ValueWithUnitSchema.optional(),
  /**
   * Whether the flow rate was entered by the user (true) or should come from
   * the hydraulic solver (false). Null means the user hasn't made a choice yet
   * (legacy/uninitialized state — maps to boost::optional<bool> = boost::none).
   *
   * When true, `flow_rate` contains the user-entered value.
   * When false, `flow_rate` is cleared — the engine uses the hydraulic result.
   * Only relevant when mode is NOT 'calculate_flow_rate'.
   *
   * Serialized as boost::optional<bool> in v21.0:
   *   Archive tokens: `0` = none, `1 0` = false, `1 1` = true.
   */
  is_flow_rate_user_entered: z.boolean().nullable(),
  /**
   * PF-connect-native target/source temperatures for thermal modes.
   * Not present in PIPE-FLO's boost stream — lost on `.pipe` export by design.
   * `outlet_temperature` is the T_out target for the fixed-outlet modes
   * (calculate_heat_transfer_rate, calculate_inlet_temperature) and the flow
   * mode; `inlet_temperature` is a flow-mode input (Increment 3). Absolute units.
   */
  inlet_temperature: ValueWithUnitSchema.optional(),
  outlet_temperature: ValueWithUnitSchema.optional(),
  /**
   * Import-time marker: this device's flow is already pinned by a linked TCV
   * that was rewritten to a `flow_mass` demand (see
   * `convertTcvTemperatureControlToFlowMass`). The calc-engine suppresses
   * device-side flow injection when set, so exactly one flow constraint
   * exists per series segment. Not authored in the UI; PF-connect-native.
   */
  flow_pinned_by_linked_valve: z.boolean().optional(),
});

export type ThermalCalculation = z.infer<typeof ThermalCalculationSchema>;

/**
 * Unmodeled side of a 2-pipe heat exchanger thermal calculation.
 * Extends ThermalCalculationSchema with inlet/outlet fluid zone references.
 *
 * C++ class hierarchy: thermal_unmodeled : thermal_calculation
 * Serialized as: base_object<thermal_calculation> + _inlet_fluid_zone + _outlet_fluid_zone
 *
 * BOOST_CLASS_VERSION(thermal_unmodeled, 1)
 */
export const ThermalCalculationUnmodeledSchema = ThermalCalculationSchema.extend({
  /**
   * Fluid zone for the unmodeled side inlet. Contains fluid properties
   * including temperature. References a fluid_zone in the document-level
   * fluid_zones array. Serialized as a Boost pointer reference.
   */
  inlet_fluid_zone_id: z.string().optional(),
  /**
   * Fluid zone for the unmodeled side outlet.
   */
  outlet_fluid_zone_id: z.string().optional(),
});

export type ThermalCalculationUnmodeled = z.infer<typeof ThermalCalculationUnmodeledSchema>;

/**
 * Thermal calculation for 2-pipe heat exchangers.
 * Combines the hydraulically modeled side with the unmodeled side.
 *
 * Source: C++ thermal_calculation_hx_2_pipe
 */
export const ThermalCalculationHeatExchangerSchema = z.object({
  modeled_side: ThermalCalculationSchema,
  unmodeled_side: ThermalCalculationUnmodeledSchema,
});

export type ThermalCalculationHeatExchanger = z.infer<typeof ThermalCalculationHeatExchangerSchema>;
